import React, { useEffect, useState } from 'react';
import EmployeeApi from '../employee/employeeApi';
import { Employee } from '../employee/employeeType';
import JobsiteApi from '../jobsite/jobsiteApi';
import { Jobsite } from '../jobsite/jobsiteType';
import TimecardApi from '../timecard/timecardApi';
import { useNavigate } from 'react-router-dom';
import DurationSelector from './durationSelector';
import { ReportData } from '../timecard/timecardType';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import TimecardReportTable from './TimecardReportTable';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';

const TimecardReportPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
    { label: 'Employee', path: '/main/company/employee' },
    { label: 'Timecard Report', path: '/main/company/employee/timecard_report' },
  ];

  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string | null>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeCode, setEmployeeCode] = useState<string>('');
  const [jobsites, setJobsites] = useState<Jobsite[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [jobsiteId, setJobsiteId] = useState<string>('');
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    const fetchJobsite = async () => {
      try {
        const fetchedJobsites = await JobsiteApi.get();
        setJobsites(fetchedJobsites);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchJobsite();
  }, []);

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const fetchedEmployees = await EmployeeApi.get();
        setEmployees(fetchedEmployees);
        if (fetchedEmployees.length > 0) {
          setEmployeeCode(fetchedEmployees[0].employee_code);
        }
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };

    fetchEmployee();
  }, []);

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const listOfJobsites = jobsites.map((jobsite) => (
    <option key={jobsite.jobsite_id} value={jobsite.jobsite_id}>
      {jobsite.name}
    </option>
  ));

  const handleEmployeeSelect = (employeeCode: string) => {
    setEmployeeCode(employeeCode);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setErrorMessage('');

    if (name === 'hourly_rate') {
      const parsedValue = parseFloat(value);

      if (isNaN(parsedValue)) {
        setErrorMessage('Hourly rate must be a valid number');
        return;
      } else {
        setHourlyRate(value);
      }
    } else if (name === 'jobsite_id') {
      setJobsiteId(value);
    }
  };

  const handleViewReport = async () => {
    setErrorMessage('');

    if (!startDate || !endDate) {
      setErrorMessage('Please select duration');
      return;
    }
    if (!employeeCode) {
      setErrorMessage('Please select employee');
      return;
    }

    try {
      const response = await TimecardApi.getTimecardReport(startDate, endDate, employeeCode, jobsiteId, hourlyRate);
      const reportData = response.timecards;
      setReportData(reportData);
      setShowReport(true);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Employee Time Card Report</h2>

        {showReport && reportData.length === 0 && (
          <Alert variant="info">No timecard data available.</Alert>
        )}

        {message && message.trim() !== '' && <Alert variant="success">{message}</Alert>}

        <form onSubmit={handleSubmit} className="space-y-6">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Row 1: Jobsite and Duration side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Number(jobsites.length) > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="jobsite_id">Select Jobsite:</Label>
                <Select id="jobsite_id" name="jobsite_id" onChange={handleInputChange}>
                  <option value="">All Jobsite</option>
                  {listOfJobsites}
                </Select>
              </div>
            ) : (
              <Alert variant="error">No Jobsites found. Please create jobsite first.</Alert>
            )}

            <div>
              <DurationSelector onDateChange={handleDateChange} />
            </div>
          </div>

          {/* Row 2: Employee and Hourly Rate side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {employees.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="employeeSelect">Select Employee:</Label>
                  <span className="text-red-600 font-semibold">*</span>
                </div>
                <Select
                  id="employeeSelect"
                  value={employeeCode}
                  onChange={(e) => handleEmployeeSelect(e.target.value)}
                >
                  {employees.map((employee) => (
                    <option key={employee.employee_code} value={employee.employee_code}>
                      {employee.first_name} {employee.last_name}
                    </option>
                  ))}
                  <option value="all">All Employees</option>
                </Select>
              </div>
            ) : (
              <Alert variant="error">
                No employee found, please click "Company - Employee - New" to create employee
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate (US $) for Payment Calculation:</Label>
              <Input
                type="text"
                id="hourly_rate"
                name="hourly_rate"
                onChange={handleInputChange}
                placeholder="Enter hourly rate"
              />
            </div>
          </div>

          <div>
            <Button type="button" size="medium" onClick={handleViewReport}>
              View Report
            </Button>
          </div>

          <div>
            <TimecardReportTable reportData={reportData} />
          </div>
        </form>
      </div>
    </>
  );
};

export default TimecardReportPage;